import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as pbi from 'powerbi-client';

import { ClientPowerBIReport } from '@power-bi/models/client-power-bi-report';
import { EmbedToken } from '@power-bi/models/embed-token';
import { PowerBIEmbedService } from '@power-bi/services/power-bi-embed.service';

declare let powerbi: pbi.service.Service;

@Component({
  selector: 'app-power-bi-embed',
  templateUrl: './power-bi-embed.component.html',
  styleUrls: ['./power-bi-embed.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PowerBiEmbedComponent {
  readonly #powerBIEmbedService = inject(PowerBIEmbedService);
  readonly #destroyRef = inject(DestroyRef);

  readonly selectedReport = input<ClientPowerBIReport | null>(null);

  readonly embeddedReport = output<pbi.Embed>();

  readonly errorOccurred = signal(false);

  constructor() {
    effect(() => {
      const report = this.selectedReport();
      if (!report) {
        return;
      }

      this.embedReport(report);
    });
  }

  //#region Private methods

  private embedReport(clientPowerBIReport: ClientPowerBIReport): void {
    this.errorOccurred.set(false);

    this.#powerBIEmbedService
      .getEmbedToken(clientPowerBIReport.id)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (embedToken: EmbedToken) => {
          this.onEmbedReport(clientPowerBIReport, embedToken);
        },
        error: () => {
          this.errorOccurred.set(true);
        }
      });
  }

  private onEmbedReport(
    clientPowerBIReport: ClientPowerBIReport,
    embedToken: EmbedToken
  ): void {
    const reportContainer = document.getElementById('reportContainer');

    if (!reportContainer || !embedToken) {
      if (reportContainer) {
        powerbi.reset(reportContainer);
      }
      this.errorOccurred.set(true);
      return;
    }

    const config: pbi.IEmbedConfiguration = {
      type: 'report',
      tokenType: pbi.models.TokenType.Embed,
      accessToken: embedToken.token,
      embedUrl: clientPowerBIReport.embedUrl,
      id: clientPowerBIReport.reportId,
      permissions: pbi.models.Permissions.All,
      settings: {
        filterPaneEnabled: true,
        navContentPaneEnabled: true
      }
    };

    this.embeddedReport.emit(powerbi.embed(reportContainer, config));
  }

  //#endregion
}
